// Pure calculation helpers for the Home (consolidated Business Overview)
// screen. Everything here derives from state.dashboardTransactions (already
// loaded company-wide, no site filter -- see loadDashboardTransactions() in
// src/dashboard/projectHealth.js) and state.sites. No new Supabase query, no
// new data source, no site-selection dependency.

import { monthBounds, todayISO } from '../ui/format.js';
import { filterByDateRange } from '../reports/reports.js';
import { computeTotals } from '../pnl/pnl.js';

export const HOME_DATE_FILTERS = [
  { key: 'today', label: 'Today' },
  { key: 'month', label: 'This Month' },
  { key: 'lastMonth', label: 'Last Month' },
  { key: 'year', label: 'This Year' },
  { key: 'all', label: 'All Time' }
];

function addDaysISO(dateStr, days) {
  const d = new Date(`${dateStr}T00:00:00`);
  d.setDate(d.getDate() + days);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

// { from, to, prevFrom, prevTo } for the given Home date-filter key.
// prevFrom/prevTo are null when there's no meaningful previous period
// (All Time), which callers use to skip the "% vs last period" delta.
export function homeDateRange(filterKey) {
  const today = todayISO();

  if (filterKey === 'today') {
    const yesterday = addDaysISO(today, -1);
    return { from: today, to: today, prevFrom: yesterday, prevTo: yesterday };
  }

  if (filterKey === 'lastMonth') {
    const firstOfThisMonth = monthBounds(today).first;
    const { first, last } = monthBounds(addDaysISO(firstOfThisMonth, -1));
    const prev = monthBounds(addDaysISO(first, -1));
    return { from: first, to: last, prevFrom: prev.first, prevTo: prev.last };
  }

  if (filterKey === 'year') {
    const y = Number(today.slice(0, 4));
    return { from: `${y}-01-01`, to: `${y}-12-31`, prevFrom: `${y - 1}-01-01`, prevTo: `${y - 1}-12-31` };
  }

  if (filterKey === 'all') {
    return { from: '', to: '', prevFrom: null, prevTo: null };
  }

  // 'month' (default)
  const { first, last } = monthBounds(today);
  const prev = monthBounds(addDaysISO(first, -1));
  return { from: first, to: last, prevFrom: prev.first, prevTo: prev.last };
}

export function filterForRange(transactions, from, to) {
  return filterByDateRange(transactions, 'entry_date', from, to);
}

// % change vs the previous period. null when there's no previous period, or
// the previous total was zero (a % change would be meaningless/infinite) --
// callers render nothing in that case rather than a misleading number.
export function pctDelta(curr, prev) {
  if (prev === null || prev === undefined || !prev) return null;
  return ((curr - prev) / Math.abs(prev)) * 100;
}

// One row per calendar day in [from, to] inclusive, zero-filled for days with
// no transactions -- what Income vs Expense plots for short ranges (Today /
// This Month / Last Month). Longer ranges (This Year / All Time) use
// monthlyBreakdown() from reports.js instead (see homeView.js).
export function dailyBreakdown(transactions, from, to) {
  const byDay = {};
  for (const t of transactions) {
    const d = t.entry_date;
    if (!d) continue;
    if (!byDay[d]) byDay[d] = [];
    byDay[d].push(t);
  }
  const rows = [];
  let cursor = from;
  let guard = 0;
  while (cursor <= to && guard < 400) {
    rows.push({ day: cursor, ...computeTotals(byDay[cursor] || []) });
    cursor = addDaysISO(cursor, 1);
    guard += 1;
  }
  return rows;
}

// Calendar-style label ("1 Jun") for a 'YYYY-MM-DD' day -- matches the
// reference's chart axis style, not just a bare day number.
export function dayLabel(dayStr) {
  return new Date(`${dayStr}T00:00:00`).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });
}

// { [siteId]: { income, expense } } -- the one place both Top Clients and
// Active Projects/Clients get their numbers from, so they can never diverge
// or double-count against each other.
export function siteTotals(transactions) {
  const bySite = {};
  for (const t of transactions) {
    const amt = Number(t.amount) || 0;
    if (!bySite[t.site_id]) bySite[t.site_id] = { income: 0, expense: 0 };
    if (t.type === 'income') bySite[t.site_id].income += amt;
    else if (t.type === 'expense') bySite[t.site_id].expense += amt;
  }
  return bySite;
}

// Sites ranked by income (desc) for "Active Projects / Clients". Status
// comes straight from sites.status -- the only per-site status this schema
// has -- NOT from quotation/budget health, which is a separate concept
// (see healthStatus() in projectHealth.js).
export function activeProjectsList(transactions, sites) {
  const bySite = siteTotals(transactions);
  return sites
    .map((s) => ({ id: s.id, name: s.name, client_name: s.client_name, status: s.status, ...(bySite[s.id] || { income: 0, expense: 0 }) }))
    .sort((a, b) => b.income - a.income);
}

// This schema has no clients table -- "client" is sites.client_name, a free
// text field. Multiple sites can share the same client_name (one client,
// several projects); this groups and sums across all of them so "Top
// Clients" genuinely means client, not project/site.
export function topClientsByIncome(transactions, sites) {
  const bySite = siteTotals(transactions);
  const byClient = {};
  for (const s of sites) {
    const key = (s.client_name || '').trim() || 'Unspecified Client';
    const totals = bySite[s.id] || { income: 0, expense: 0 };
    if (!byClient[key]) byClient[key] = { client: key, income: 0, expense: 0 };
    byClient[key].income += totals.income;
    byClient[key].expense += totals.expense;
  }
  return Object.values(byClient)
    .filter((c) => c.income > 0 || c.expense > 0)
    .map((c) => ({ ...c, profit: c.income - c.expense }))
    .sort((a, b) => b.income - a.income);
}
